import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft } from "react-feather";
import toast from "react-hot-toast";
import { useCreateStockOutMutation } from "../../stockApi";
import { useGetBranchesQuery } from "../../../admin/adminApi";
import { useClaims } from "../../../auth/useClaims";
import { useCanCreateStockOut } from "../../useStockPermissions";
import EmployeePicker from "../../../users/components/EmployeePicker";
import PartyPicker from "../../components/PartyPicker";
import LineItemsEditor, { emptyRow, computeTotals } from "../../components/LineItemsEditor";
import { paths } from "../../../../routes/paths";

// Rate is optional here (schema: UnitRate/LineAmount both nullable) - stock
// issued internally to an employee often has no per-unit cost tracked, only
// quantity. No Sales Order link field - no Sales module exists in React yet
// to pick one from, so SalesOrderRecordGuid is simply never sent.
// showLastPurchasePrice: a one-click "fill from last purchase price" per
// line (GET /stock/last-purchase-price) - the issue price commonly mirrors
// what was last paid for the product, and this is the one document where
// that reference is actually useful (LPO lines are typed fresh, Stock-In
// prefills its own rate from the LPO).
const columns = { showRate: true, showVat: false, showRemarks: false, showReason: false, showMaxQty: false, showLastPurchasePrice: true };
const toDateInput = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "");

const StockOutForm = () => {
  const navigate = useNavigate();
  const canCreate = useCanCreateStockOut();
  const { branches: callerBranchCodes, isAllBranches } = useClaims();
  const { data: branches = [] } = useGetBranchesQuery();
  const [createStockOut, { isLoading: isSaving }] = useCreateStockOutMutation();

  const availableBranches = isAllBranches
    ? branches
    : branches.filter((b) => callerBranchCodes?.includes(b.branchCode));

  const [branchId, setBranchId] = useState("");
  const [issuedToType, setIssuedToType] = useState("EMPLOYEE");
  const [employeeOption, setEmployeeOption] = useState(null);
  const [customerOption, setCustomerOption] = useState(null);
  const [issuedForPurpose, setIssuedForPurpose] = useState("");
  const [stockOutDate, setStockOutDate] = useState(toDateInput(new Date()));
  const [remarks, setRemarks] = useState("");
  const [lines, setLines] = useState([emptyRow()]);

  const totals = computeTotals(lines, columns);
  const readOnly = !canCreate;

  const handleSave = async () => {
    if (!branchId) return toast.error("Branch is required");
    if (issuedToType === "EMPLOYEE" && !employeeOption) return toast.error("Select the employee stock is being issued to");
    if (issuedToType === "CUSTOMER" && !customerOption?.customer) return toast.error("Select the customer stock is being issued to");
    const validLines = lines.filter((l) => l.productCode && Number(l.qty) > 0);
    if (validLines.length === 0) return toast.error("Add at least one line with a product and quantity");

    const body = {
      branchId: Number(branchId),
      issuedToType,
      issuedToEmployeeRecordGuid: issuedToType === "EMPLOYEE" ? employeeOption.value : null,
      issuedToEmployeeName:
        issuedToType === "EMPLOYEE"
          ? `${employeeOption.employee?.empName || ""} ${employeeOption.employee?.empLastName || ""}`.trim()
          : null,
      issuedToCustomerId: issuedToType === "CUSTOMER" ? customerOption.customer.custId : null,
      issuedToCustomerBranchId: issuedToType === "CUSTOMER" ? customerOption.customer.branchId : null,
      issuedToCustomerName: issuedToType === "CUSTOMER" ? customerOption.customer.custName : null,
      salesOrderRecordGuid: null,
      issuedForPurpose: issuedForPurpose.trim() || null,
      stockOutDate: new Date(stockOutDate).toISOString(),
      totalAmount: totals.netAmount,
      remarks: remarks.trim() || null,
      lines: validLines.map((l) => ({
        productCode: Number(l.productCode),
        productName: l.productName,
        issuedQty: Number(l.qty),
        unit: l.unit || null,
        unitRate: l.rate === "" ? null : Number(l.rate),
        lineAmount: l.rate === "" ? null : (Number(l.qty) || 0) * (Number(l.rate) || 0),
        barcodeValue: null,
      })),
    };

    try {
      const created = await createStockOut(body).unwrap();
      toast.success("Stock-Out posted");
      navigate(paths.stockOutDetail(created.recordGuid), { replace: true });
    } catch (error) {
      toast.error(error?.message || "Could not post the Stock-Out");
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <Link to={paths.stockOut} className="d-inline-flex align-items-center mb-2"><ArrowLeft size={16} className="me-1" /> Back to Stock-Out</Link>
              <h4>New Stock-Out</h4>
            </div>
          </div>
        </div>

        {readOnly && <div className="alert alert-info">You have read-only access. Posting a Stock-Out requires Stock.CreateStockOut.</div>}

        <div className="card mb-4">
          <div className="card-body">
            <div className="row">
              <div className="col-md-3 mb-3">
                <label className="form-label">Branch</label>
                <select className="form-control" value={branchId} onChange={(e) => setBranchId(e.target.value)} disabled={readOnly}>
                  <option value="">--Select--</option>
                  {availableBranches.map((b) => (
                    <option key={b.branchId} value={b.branchId}>{b.branchCode}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">Issued To</label>
                <select
                  className="form-control"
                  value={issuedToType}
                  onChange={(e) => { setIssuedToType(e.target.value); setEmployeeOption(null); setCustomerOption(null); }}
                  disabled={readOnly}
                >
                  <option value="EMPLOYEE">Employee</option>
                  <option value="CUSTOMER">Customer</option>
                </select>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{issuedToType === "EMPLOYEE" ? "Employee" : "Customer"}</label>
                {issuedToType === "EMPLOYEE" ? (
                  <EmployeePicker value={employeeOption} onChange={setEmployeeOption} />
                ) : (
                  <PartyPicker mode="all-branches" value={customerOption} onChange={setCustomerOption} disabled={readOnly} />
                )}
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Purpose</label>
                <input type="text" className="form-control" value={issuedForPurpose} onChange={(e) => setIssuedForPurpose(e.target.value)} disabled={readOnly} />
              </div>
              <div className="col-md-2 mb-3">
                <label className="form-label">Stock-Out Date</label>
                <input type="date" className="form-control" value={stockOutDate} onChange={(e) => setStockOutDate(e.target.value)} disabled={readOnly} />
              </div>
            </div>

            <hr />
            <h5 className="mb-3">Issued Lines</h5>
            <LineItemsEditor branchId={branchId ? Number(branchId) : undefined} rows={lines} onChange={setLines} columns={columns} disabled={readOnly} />

            <div className="row mt-3">
              <div className="col-md-8 mb-3">
                <label className="form-label">Remarks</label>
                <textarea className="form-control" rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={readOnly} />
              </div>
              <div className="col-md-4 mb-3 d-flex align-items-end justify-content-end">
                <div>Total Amount: <strong>{totals.netAmount.toFixed(2)}</strong></div>
              </div>
            </div>

            {!readOnly && (
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isSaving}>Save</button>
                <button type="button" className="btn btn-secondary" onClick={() => navigate(paths.stockOut)}>Cancel</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockOutForm;
